import { defineStore } from "pinia";
import { rootGetters } from "@/main";

import { moveItemToFront } from "@/services/utils/Arrays"
import { useShopsStore as useShopsDictionaryStore } from "@/store/catalog_data/shops";

const endpoints = {
    getShops: { method: "get", url: "/catalog_data/shops" },
    getShop: { method: "get", url: "/catalog_data/shops/{shop_id}" },
    deleteShop: { method: "delete", url: "/catalog_data/shops/{shop_id}" },
    createShop: { method: "post", url: "/catalog_data/shops" },
    updateShop: { method: "put", url: "/catalog_data/shops/{shop_id}" },
};

export const useShopsStore = defineStore("shops", {
    state: () => ({
        shopsList: [] as ShopFromServer[],
        shopsCount: 0,
        shopsPerPage: 1,
        activeFirst: false,
        addedId: 0,
    }),
    getters: {
        shopsPagesCount: (state) => {
            return Math.ceil(state.shopsCount / state.shopsPerPage)
        }
    },
    actions: {
        async createShop(shop: BaseShop) {
            await rootGetters.api
                .proceedRequest(endpoints.createShop, {}, {}, shop)
                .then((response) => {
                    if (response) {
                        this.addedId = response.data.id;
                        this.activeFirst = true;

                        // Обновляем словарь магазинов
                        const shopsDictionaryStore = useShopsDictionaryStore();
                        shopsDictionaryStore.addOrUpdateItem({
                            id: response.data.id,
                            shop_pk: response.data.shop_pk || "",
                            shop_name: response.data.shop_name,
                            shop_short_name: response.data.shop_short_name,
                            shop_category: response.data.shop_category,
                            shop_inn: response.data.shop_inn,
                            has_data_from_providers: response.data.has_data_from_providers ?? false,
                        });

                        rootGetters.toast.show(
                            "info",
                            "Создание",
                            `Магазин "${response.data.shop_name}" успешно сохранен в раздел "Список магазинов".`
                        );
                    }
                }).catch((err) => {
                    return Promise.reject(err);
                })
        },
        async getShop(shopId: number) {
            return await rootGetters.api
                .proceedRequest(endpoints.getShop, { shop_id: shopId })
                .then((response) => {
                    return response.data;
                });
        },
        async getShops(params: ShopsParams = { page: 1, per_page: 20 }) {
            await rootGetters.api.proceedRequest(
                endpoints.getShops, {}, params
            ).then((response) => {
                this.shopsList = response.data;
                if (this.shopsList) {
                    this.shopsPerPage = parseInt(response.headers["x-per-page"]);
                    this.shopsCount = parseInt(response.headers["x-total-count"]);

                    if (this.addedId > 0) {
                        moveItemToFront(this.shopsList, this.addedId);
                        this.addedId = 0;
                    }
                }
            });
        },
        async updateShop(shop: BaseShop, shopId: number) {
            await rootGetters.api
                .proceedRequest(endpoints.updateShop, { shop_id: shopId }, {}, shop)
                .then((response) => {
                    if (response) {
                        this.addedId = response.data.id;
                        this.activeFirst = true;

                        rootGetters.toast.show(
                            "info",
                            "Обновление",
                            `Магазин "${response.data.shop_name}" успешно обновлен.`
                        );
                    }
                }).catch((err) => {
                    return Promise.reject(err);
                })
        },
        async deleteShop(shopId: number) {
            await rootGetters.api
                .proceedRequest(endpoints.deleteShop, {
                    shop_id: shopId,
                })
        },
    }
})

export interface BaseShop {
    shop_pk?: string;
    shop_name: string;
    shop_short_name: string | null;
    shop_inn: string;
    category_id: number;
    has_data_from_providers?: boolean;
}

export interface ShopFromServer extends BaseShop {
    id: number;
    shop_category: string | null;
}

export interface ShopsParams {
    page: number;
    per_page: number;

    name?: string;
    shop_name__ilike?: string;
    shop_short_name__ilike?: string;
    shop_inn?: string;
    shop_pk?: string;
    cdate?: string;
    mdate?: string;
    cuser__id__in?: string;
    categories__category_id__in?: string;

    order_by?: string;

    has_data_from_providers?: boolean;
}

